import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Resolution, ResolutionTypes } from '../../../chart-kit/shared/types'
import useFetchCostSavingDataManual from './use-fetch-cost-saving-data-manual'
import { useIsDynamicEmsAccessible } from 'src/Libs/dynamic-power-prices'
import { useTypedSelector } from 'src/State/RootReducer'
import useDynamicPricesSettings from 'src/Libs/dynamic-power-prices/hooks/use-dynamic-prices-settings'
import { ChartView } from '../../../chart-kit/shared/utils'
import { useDynamicEmsData } from 'src/Libs/dynamic-power-prices/hooks/dynamic-ems/use-dynamic-ems-data'
import { getCostSavingChartDataWithDynamicEms } from 'src/Libs/dynamic-power-prices/utils/cost-saving-chart'
import { dayViewLabelConfig, emsLabelConfig, labelConfig } from 'src/Libs/dynamic-power-prices/const'
import {
  CostSavingChartData,
  CsvData,
  EmsBatteryMode,
  CostSavingChartDataWithDynamicEms,
  CostSavingChartExportData,
} from 'src/Libs/dynamic-power-prices/types'
import { TFunction } from 'i18next'
import { format } from '@e3dc-react/shell/Libs/date'
import { round } from 'lodash'
import { getLocalizedNumber } from 'src/Libs/languages/functions/default'
import { getTraceLabel, LabelConfig } from '../../../chart-kit/shared/hooks/use-csv-link'
import { LabelKeyObject } from 'react-csv/lib/core'

interface ReturnType {
  getCostSavingExportData: (resolution: Resolution) => Promise<CostSavingsExportDataParams>
  isFetching: boolean
}

export interface CostSavingsExportDataParams {
  csvData: Partial<CsvData>[]
  csvHeaders: LabelKeyObject[]
}

/**
 * Exposes logic to fetch and transform cost saving data for csv export.
 */
const useCostSavingExportData = (view: ChartView): ReturnType => {
  const { t } = useCustomTranslation()
  const { isDynamicEmsAccessible } = useIsDynamicEmsAccessible()
  const storageId = useTypedSelector(state => state.storage.activeStorage)
  const { dynamicPricesSettings } = useDynamicPricesSettings({ storageId: storageId ?? '' })
  const isDayView = view === 'day'
  const showDynamicEmsReferenceAreas = isDayView && isDynamicEmsAccessible && !!dynamicPricesSettings?.isDynamicEmsEnabledForBattery
  const installationId = useTypedSelector(store => store.storage.installation?.id)?.toString() ?? ''
  const { data: dynamicEmsData } = useDynamicEmsData(installationId)
  const { handleFetchCostSavingsData, isFetching } = useFetchCostSavingDataManual()

  const getCostSavingExportData = async (resolution: Resolution): Promise<CostSavingsExportDataParams> => {
    const data = await handleFetchCostSavingsData(resolution)
    const dataToUse = showDynamicEmsReferenceAreas ? getCostSavingChartDataWithDynamicEms(data, dynamicEmsData) : data
    const labelConfigToUse = getLabelConfig(isDayView, showDynamicEmsReferenceAreas)

    const csvData = dataToUse.map(dataPoint =>
      transformDataForCsv(dataPoint, isDayView, showDynamicEmsReferenceAreas, resolution === ResolutionTypes.FifteenMinutes, t)
    )

    const csvHeaders = Object.keys(labelConfigToUse).map(key => ({ key, label: getTraceLabel(key, labelConfigToUse, t) }))

    return { csvData, csvHeaders }
  }

  return { getCostSavingExportData, isFetching }
}

export default useCostSavingExportData

const getLabelConfig = (isDayView: boolean, showDynamicEmsReferenceAreas: boolean): LabelConfig<Partial<CostSavingChartExportData>> => {
  if (isDayView && showDynamicEmsReferenceAreas) return emsLabelConfig
  if (isDayView) return dayViewLabelConfig
  return labelConfig
}

const replaceRowDataWithTranslationKey = (label: EmsBatteryMode, t: TFunction): string => {
  if (label === EmsBatteryMode.BlockDischarging) return t('dynamic-ems.cost-saving-chart.legend.block-discharging')
  if (label === EmsBatteryMode.GridCharge) return t('dynamic-ems.cost-saving-chart.legend.grid-charge')
  return t('dynamic-ems.cost-saving-chart.legend.normal-operation')
}

/**
 * Transforms the cost saving data to a format suitable for csv export.
 * Note that traces included in data and export differ based on the view (day or not) and if dynamic ems is enabled.
 */
const transformDataForCsv = (
  data: CostSavingChartData | CostSavingChartDataWithDynamicEms,
  isDayView: boolean,
  showDynamicEmsReferenceAreas: boolean,
  shouldUseHourFormat: boolean,
  t: TFunction
): Partial<CsvData> => {
  const canHaveSavings = Boolean(data.consumptionPrice)
  const isFutureDate = new Date(data.time) > new Date()
  const notApplicableText = t('common.notAvailable')

  if (isDayView && showDynamicEmsReferenceAreas) {
    return {
      time: format(new Date(data.time), t('common.format.date-fns-date-time')),
      consumptionPrice: canHaveSavings ? round(data.consumptionPrice as number).toString() : notApplicableText,
      batteryMode: 'batteryMode' in data && data.batteryMode ? replaceRowDataWithTranslationKey(data.batteryMode, t) : notApplicableText,
    }
  }

  if (isDayView) {
    return {
      time: format(new Date(data.time), t('common.format.date-fns-date-time')),
      consumptionPrice: canHaveSavings ? round(data.consumptionPrice as number).toString() : notApplicableText,
    }
  }

  if (isFutureDate) return {}

  return {
    time: shouldUseHourFormat
      ? format(new Date(data.time), t('common.format.date-fns-date-time'))
      : format(new Date(data.time), t('common.format.date-fns-date')),
    consumptionPrice: !isFutureDate && canHaveSavings ? getLocalizedNumber(round(data.consumptionPrice as number), 2, 'de') : notApplicableText,
    threeSixtyBatteryProducedEnergy: !isFutureDate ? getLocalizedNumber(data.threeSixtyBatteryProducedEnergy ?? 0, 3, 'de') : notApplicableText,
    threeSixtyBatterySavings:
      !isFutureDate && canHaveSavings ? getLocalizedNumber(round(data.threeSixtyBatterySavings ?? 0, 2), 2, 'de') : notApplicableText,
    threeSixtyWallboxProducedEnergy: !isFutureDate ? getLocalizedNumber(data.threeSixtyWallboxProducedEnergy ?? 0, 3, 'de') : notApplicableText,
    threeSixtyWallboxSavings:
      !isFutureDate && canHaveSavings ? getLocalizedNumber(round(data.threeSixtyWallboxSavings ?? 0, 2), 2, 'de') : notApplicableText,
  }
}
